import UserPublicResponseResource from "common/resources/User/UserPublicResponseResource";
import { ReactNode, createContext, useContext, useEffect, useState, useRef } from "react";
import { AuthContext, IAuthContext } from "./Auth";
import { container } from "tsyringe";
import UserService from "services/UserService";
import DeferredPromise from "utils/DeferredPromise";

export type IUserContext = {
	isLoading: boolean;
	user: UserPublicResponseResource | null;
	updateUser: (user: UserPublicResponseResource | null) => void;
	login: IAuthContext["login"];
	logout: IAuthContext["logout"];
	register: IAuthContext["register"];
};

type IProps = {
	children: ReactNode;
};

export const UserContext = createContext<IUserContext>(undefined!);

const userService = container.resolve(UserService);

export function UserProvider(props: IProps) {
	const { jwtContent, isLoading: isAuthLoading, login, logout, register } = useContext(AuthContext);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [user, setUser] = useState<UserPublicResponseResource | null>(null);
	const isLoadingFinished = useRef<DeferredPromise<void> | null>(DeferredPromise.new());

	useEffect(() => {
		setIsLoading(true);

		isLoadingFinished.current ??= DeferredPromise.new<void>();
		if (!jwtContent) {
			setUser(null);
			setIsLoading(false);
			isLoadingFinished.current.resolve();
			isLoadingFinished.current = null;
			return;
		}

		userService
			.getByEmail(jwtContent.email)
			.then((user) => setUser(jwtContent ? user : null))
			.finally(() => {
				setIsLoading(false);
				isLoadingFinished.current?.resolve();
				isLoadingFinished.current = null;
			});
	}, [jwtContent]);

	/**
	 * If the loading is not finished, we wait for it to finish before logging out
	 * Sometimes, the user still loadind when the logout is called
	 * It's happening when we try to logout at beginning of the app
	 */
	function _logout() {
		if (isLoadingFinished.current) return isLoadingFinished.current.promise.then(() => logout());
		return logout();
	}

	function _login(...args: Parameters<IAuthContext["login"]>) {
		if (isLoadingFinished.current) return isLoadingFinished.current.promise.then(() => login(...args));
		return login(...args);
	}

	function _register(...args: Parameters<IAuthContext["register"]>) {
		if (isLoadingFinished.current) return isLoadingFinished.current.promise.then(() => register(...args));
		return register(...args);
	}

	return (
		<UserContext.Provider
			value={{
				isLoading: isAuthLoading || isLoading,
				user,
				updateUser: setUser,
				login: _login,
				logout: _logout,
				register: _register,
			}}>
			{props.children}
		</UserContext.Provider>
	);
}
